<script setup lang="ts">
import { useCurrentUser } from "@/api/current-user";
import { useStationCreation } from "@/api/station-query";
import StationSerialNumberInput from "@/components/stations/StationSerialNumberField.vue";
import { useSnackbarStore } from "@/stores";
import { useForm } from "@soft-db/form";
import { CreateStation, Station } from "@soft-db/monitoring-api-client";
import { FormField, Icon, InputGroup, TextInput } from "@soft-db/ui";
import { useI18n } from "vue-i18n";
import { z } from "zod";
import AppDialog from "../common/AppDialog.vue";
import { icRemoteOutline } from "../icons.ts";

const dialog = defineModel<boolean>("dialog");

const emit = defineEmits<{
	created: [id: string];
}>();

const { t } = useI18n();

const { currentUser } = useCurrentUser();
const stationCreation = useStationCreation();
const snackbarStore = useSnackbarStore();

const { useField, handleSubmit, clear, validateAll } = useForm<CreateStation>();
const name = useField("name", { schema: z.string().trim().min(1) });

const createStation = handleSubmit(async (form) => {
	if (!currentUser.value) return;
	if (currentUser.value.scope.type !== "distributor") return;

	validateAll();

	form.distributor = currentUser.value.scope.id;
	await stationCreation.mutateAsync(form).then((station: Station) => {
		snackbarStore.openOnMessage(t("snackbar.stationCreated"));
		emit("created", station.id);
		close();
	});
});

function close() {
	dialog.value = false;
	clear();
}
</script>

<template>
	<AppDialog
		v-model="dialog"
		class="w-[500px]"
		:actionText="t('common.create')"
		@click:action="createStation"
		@close="close"
	>
		<template #title>
			<Icon :d="icRemoteOutline" />
			<span>{{ t("actions.createStation") }} </span>
		</template>
		<template #content>
			<FormField :label="t('common.name')" :errors="name.errors.value">
				<InputGroup>
					<TextInput v-model="name.data.value" />
				</InputGroup>
			</FormField>

			<StationSerialNumberInput field="serialNumber" hideIcon />
		</template>
	</AppDialog>
</template>
